import React from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import {useDataProps, useAriaProps} from '@famly/mf_ts-utils_data-aria-props';
import {extractSystemStyles} from '@famly/mf_system_system-props';

/**
 * Props for the Spinner component
 */
export type SpinnerProps = {
    /**
     * The size of the spinner in pixels
     */
    size?: number;

    /**
     * Accessible label for the spinner. Required for accessibility as the spinner
     * needs to communicate its purpose to screen reader users.
     */
    'aria-label': string;

    /**
     * Custom color prop to be used only in signin app, as the signin app has the
     * branding color as background
     */
    color?: 'n0';
};

/**
 * Spinner component based on MUI CircularProgress
 * Used to indicate a loading state
 *
 * @param props - The component props
 * @returns A spinner component
 *
 * @example
 * ```tsx
 * <Spinner aria-label="Loading calendar" />
 * ```
 */
export const Spinner = (props: SpinnerProps) => {
    const {size = 24, color, ...rest} = props;
    const dataProps = useDataProps(rest);
    const ariaProps = useAriaProps(rest);

    const {sx} = extractSystemStyles({...rest, color: color ?? 'p300'});

    return <CircularProgress {...dataProps} {...ariaProps} size={size} sx={sx} />;
};
